import Character from "../character/Character";
import CharacterCreator from "../character/CharacterCreator";
import { FixtureDescriptions } from "../fixture/FixtureDescriptions";
import Grid from "../grid/Grid";
import { TileTypeMap } from "../grid/TileTypes";
import { MonsterDescriptionMap } from "../monster/MonsterDescriptions";
import GridInversionOfControl from "../grid/GridInversionOfControl";
import * as LZString from 'lz-string';
import GridTile from "../grid/GridTile";

export default class GridSave {
    /**
     * @param {CharacterCreator} characterCreator
     */
    constructor(characterCreator) {
        /**
         * @constant
         * @type {CharacterCreator}
         */
        this.characterCreator = characterCreator;
    }

    /**
     * @param {Grid} grid 
     * @returns {string}
     */
    getGridKey(grid) {
        const col = grid.getGridCol();
        const row = grid.getGridRow();

        const colSign = col >= 0 ? '' : 'n';
        const rowSign = row >= 0 ? '' : 'n';

        return 'G_' + colSign + Math.abs(col) + '_' + rowSign + Math.abs(row);
    }

    /**
     * @param {Grid} grid 
     * @returns {Object}
     */
    generateGridStateForExport(grid) {
        if (!grid) {
            console.log('GridSave.generateGridStateForExport() Null grid');
            return null;
        }

        const gridJson = this.generateState(grid);
        if (!gridJson) {
            console.log('GridSave.generateGridStateForExport() Failed to generate grid state');
            return null;
        }

        const gridKey = this.getGridKey(grid);

        return {
            key: gridKey,
            origin: {
                x: grid.origin.x,
                y: grid.origin.y
            },
            state: gridJson
        }
    }

    /**
     * @param {Grid} grid 
     * @param {Object} gridState 
     * @returns {boolean}
     */
    importGridState(grid, gridState) {

        // The grid state looks like this: The key is included.
        // {
        //     key: gridKey,
        //     origin: {x: 123, y: 123},
        //     state: gridJson
        // }

        if (!grid) {
            console.log('GridSave.importGridState() FAIL. grid is null');
            return false;
        }

        // const gridKey = gridState.key;
        // if (!gridKey) {
        //     console.log('GridSave.importGridState() FAIL. No grid key found in imported save state.');
        //     return false;
        // }

        const origin = gridState.origin;

        //////////////////////////////////////////////////////////
        // The grid origin needs to be initialized before loading
        //////////////////////////////////////////////////////////

        // Update origin
        grid.origin.set(origin.x, origin.y);

        // Initialize all the grid zones/regions/tiles for the new grid origin.
        grid.initializeZonesForGrid(); // <-- updates the origins of all zones/regions/tiles.


        this.loadStateInternal(grid, gridState.state);
        return true;
    }

    /**
     * @param {Grid} grid 
     * @returns {boolean}
     */
    saveGridState(grid) {

        // If the grid has not changed since it was loaded, then don't bother saving it again.
        if (grid.gridChangeCount === 0) {
            //console.log('GridSave.saveGridState() Grid change count is 0.');
            return;
        }

        const gridJson = this.generateState(grid);
        if (!gridJson) {
            console.log('GridSave.saveGridState() Failed to generate grid state');
            return false;
        }

        const gridKey = this.getGridKey(grid);

        const gridJsonString = JSON.stringify(gridJson); // This is super fast.  Each grid is roughly 47,000 characters

        if (!gridJsonString) {
            console.log('GridSave.saveGridState() Failed to stringify state for grid: ' + gridKey);
            return false;
        }

        const compressedStateString = LZString.compressToUTF16(gridJsonString);
        if (!compressedStateString) {
            console.log('GridSave.saveGridState() Failed to compress state for grid: ' + gridKey);
            return false;
        }
    
        localStorage.setItem(gridKey, compressedStateString);
        
        return true;
    }
    
    /**
     * Removes the save state for the grid.
     * @param {string} gridKey 
     */
    removeGridState(gridKey) {
        localStorage.removeItem(gridKey);
    }

    /**
     * @param {Grid} grid 
     * @returns {boolean} True if the grid has save data, false otherwise.
     */
    loadGridState(grid) {
        const gridKey = this.getGridKey(grid);

        const compressedStateString = localStorage.getItem(gridKey);

        if (compressedStateString) {
            const gridJsonString = LZString.decompressFromUTF16(compressedStateString);
            if (gridJsonString) {
                const gridJson = JSON.parse(gridJsonString);
                if (gridJson) {
                    this.loadStateInternal(grid, gridJson);
                    return true;
                }
                else {
                    console.log('GridSave.loadGridState() Failed to parse grid json.  grid: ' + gridKey);
                }
            }
            else {
                console.log('GridSave.loadGridState() Failed to decompress grid save state. grid: ' + gridKey);
            }
        }
        // Note: It is not an error for a grid to not have save data.

        // The grid does not have save data, we will need to generate it instead.

        return false;
    }

    /**
     * @private
     * @param {Grid} grid 
     * @return {Object}
     */
    generateState(grid) {
        const gridState = {};

        // Save things that are stored in the tiles
        gridState.tiles = this.generateTileStateArray(grid);

        // Save all the characters in the grid.
        gridState.characters = this.generateCharacterStateArray(grid);

        return gridState;
    }

    /**
     * @private
     * @param {Grid} grid 
     * @param {Object} gridState 
     */
    loadStateInternal(grid, gridState) {
        this.loadTileStateArray(grid, gridState.tiles);
        this.loadCharacterStateArray(grid, gridState.characters);
    }

    /**
     * @private
     * @param {Grid} grid 
     * @returns {Array.<Object>}
     */
    generateTileStateArray(grid) {
        const thisRef = this;
        const stateArray = [];

        GridInversionOfControl.allTilesInGrid(grid, 
            /**
             * @param {GridTile} tile 
             */
            (tile) => {
                stateArray.push(thisRef.generateTileState(tile));
            }
        );

        return stateArray;
    }

    /**
     * @private
     * @param {Grid} grid 
     * @param {Array.<Object>} tilesStateArray
     */
    loadTileStateArray(grid, tilesStateArray) {
        if (!tilesStateArray) {
            console.log('GridSave.loadTileStateArray() no tile state array');
            return;
        }

        let i = 0;
        const thisRef = this;

        GridInversionOfControl.allTilesInGrid(grid, 
            /**
             * @param {GridTile} tile 
             */
            (tile) => {
                thisRef.loadTileState(tile, tilesStateArray[i]);
                i++;
            }
        );
    }

    /**
     * @private
     * @param {Grid} grid 
     * @returns {Array.<Object>}
     */
    generateCharacterStateArray(grid) {
        const stateArray = [];

        if (grid.characters.length === 0) {
            return stateArray;
        }

        const tileCol = grid.getOriginTileCol();
        const tileRow = grid.getOriginTileRow();

        for (let i = 0; i < grid.characters.length; i++) {
            const character = grid.characters[i];
                
            // We are only saving monsters here.
            if (!character.isMonster()) {
                continue;
            }

            const characterState = this.generateCharacterState(tileCol, tileRow, character);
            if (characterState) {
                stateArray.push(characterState);
            }
        }

        return stateArray;
    }

    /**
     * @private
     * @param {Grid} grid 
     * @param {Array.<Object>} characterStateArray 
     */
    loadCharacterStateArray(grid, characterStateArray) {
        if (!characterStateArray || characterStateArray.length === 0) {
            return;
        }

        for (let i = 0; i < characterStateArray.length; i++) {
            // This will cause the character to be added to the grid
            this.loadCharacterState(grid, characterStateArray[i]);
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @returns {Object}
     */
    generateTileState(tile) {
        const tileState = {};

        let tileBits = 0;
        if (tile.open) {
            tileBits += 1;
        }
        if (tile.lighting.sunlightFull) {
            tileBits += 2;
        }
        if (tile.lighting.sunlightPartial) {
            tileBits += 4;
        }
        if (tile.lighting.everVisibleToCharacter) {
            tileBits += 8;
        }

        tileState.a = tileBits;
        tileState.b = tile.tileType.id;

        if (tile.fixtureDescription) {
            tileState.f = {
                t: tile.fixtureDescription.typeName,
                c: tile.fixtureDescription.colorName
            };
        }

        // Do not save the background sprite. We can re-generate this based on the tile type.

        return tileState;
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {Object} tileState 
     */
    loadTileState(tile, tileState) {
        if (!tileState) {
            console.log('GridSave.loadTileState() no tile state');
            return;
        }

        const tileBits = tileState.a;
        const tileTypeId = tileState.b;

        const open = (tileBits & 1) === 1;

        const tileType = TileTypeMap[tileTypeId];
        tile.tileInit(open, tileType);

        const sunlightFull = (tileBits & 2) === 2;
        const sunlightPartial = (tileBits & 4) === 4;
        const everVisibleToCharacter = (tileBits & 8) === 8;

        tile.lighting.sunlightPartial = sunlightPartial;

        ////tile.lighting.everVisibleToCharacter = everVisibleToCharacter;
        ///////tile.lighting.sunlightFull = sunlightFull;

        if (sunlightFull) {
            tile.markAsSunlightFull();
        }
        
        if (everVisibleToCharacter) {
            tile.markTileAsEverVisible();
        }

        // See if there is a fixture to load.
        if (tileState.f) {
            const typeName = tileState.f.t;
            const colorName = tileState.f.c;
            
            tile.fixtureDescription = FixtureDescriptions.getDescription(typeName, colorName)
        }
    }

    /**
     * @private
     * @param {number} gridTileCol
     * @param {number} gridTileRow
     * @param {Character} character 
     * @returns {Object}
     */
    generateCharacterState(gridTileCol, gridTileRow, character) {

        // // In some cases, the tile can be null.
        const tile = character.position.tile;
        if (!tile) {
            console.log('GridSave.generateCharacterState() character tile is null');
            return null;
        }

        const characterState = {};
        // Save the local col/row relative to the grid origin
        characterState.c = tile.getTileCol() - gridTileCol;
        characterState.r = tile.getTileRow() - gridTileRow;
        characterState.d = character.getMonsterDescriptionId();
        return characterState;
    }

    /**
     * @private
     * @param {Grid} grid
     * @param {Object} characterState 
     * @returns {Character}
     */
    loadCharacterState(grid, characterState) {
        if (!characterState) {
            return;
        }
                
        //////////////////////////////////////////////
        // Tile
        //////////////////////////////////////////////

        const tileCol = characterState.c;
        const tileRow = characterState.r;
        const monsterDescriptionId = characterState.d;

        const tile = grid.getGridTileLocal(tileCol, tileRow); // We save the local col/row to keep the values small.
        if (!tile) {
            console.log('GridSave.loadCharacterState() Failed to find tile for character.  tileCol=' + tileCol + ' tileRow=' + tileRow);
            return;
        }

        //////////////////////////////////////////////
        // Monster Description
        //////////////////////////////////////////////

        const monsterDescription = MonsterDescriptionMap[monsterDescriptionId];
        if (!monsterDescription) {
            console.log('GridSave.loadCharacterState() Failed to find monster description: ' + monsterDescriptionId);
            return;
        }

        // This will add the character to the grid.
        this.characterCreator.createMonsterAtTile(monsterDescription, tile);
    }
}